import React from 'react'
import { Alert, Button, Input, Switch, Typography } from 'antd'
import {
  DownloadOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined
} from '@ant-design/icons'
import { sellerApi } from 'api/seller'
import { IOneCSettingsResponse, TVariantSeller } from 'models/seller'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { API_URL } from 'shared/constants/urls'
import { StockConfirmationStatus } from 'components/sellers/StockConfirmationStatus/StockConfirmationStatus'
import { IntegrationActions } from 'components/sellers/IntegrationActions/IntegrationActions'
import { SectionTitle } from 'components/ui'
import styles from './SellerApiOneC.module.scss'

interface ISellerApiOneCProps {
  type: TVariantSeller
  sellerId: string
}

const ONE_C_PROCESSING_URL = `${API_URL}/1c/seller-file`
const ONE_C_VIDEO_INSTRUCTION_URL = ''
const TOKEN_PLACEHOLDER = '40a8cedcc57b2161430935fd558ad51a434a30ec'

export const SellerApiOneC: React.FC<ISellerApiOneCProps> = ({
  type,
  sellerId
}) => {
  const [isLoaded, setIsLoaded] = React.useState(false)
  const [isSavingCreateNewProducts, setIsSavingCreateNewProducts] = React.useState(false)
  const [isSavingFillDescription, setIsSavingFillDescription] = React.useState(false)
  const [oneCSettings, setOneCSettings] = React.useState<IOneCSettingsResponse | null>(null)

  React.useEffect(() => {
    const loadSeller = async () => {
      try {
        const response = await sellerApi.getSeller(sellerId)
        setOneCSettings(response.data.one_c ?? null)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoaded(true)
      }
    }

    loadSeller()
  }, [sellerId])

  const handleCopyToken = async () => {
    const token = oneCSettings?.token
    if (!token) {
      return
    }

    try {
      await navigator.clipboard.writeText(token)
      showMessage('success', 'API токен скопирован', '')
    } catch {
      showMessage('error', 'Не удалось скопировать API токен', '')
    }
  }

  const handleCreateNewProductsChange = async (checked: boolean) => {
    try {
      setIsSavingCreateNewProducts(true)
      const response = await sellerApi.updateOneCCreateNewProducts({
        create_new_products: checked,
        seller_id: type === 'seller' ? sellerId : undefined
      })
      setOneCSettings(response.data)
      showMessage('success', 'Настройки 1С сохранены', '')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSavingCreateNewProducts(false)
    }
  }

  const handleFillDescriptionChange = async (checked: boolean) => {
    try {
      setIsSavingFillDescription(true)
      const response = await sellerApi.updateOneCCreateNewProducts({
        create_new_products: Boolean(oneCSettings?.create_new_products),
        fill_description_on_create: checked,
        seller_id: type === 'seller' ? sellerId : undefined
      })
      setOneCSettings(response.data)
      showMessage('success', 'Настройки 1С сохранены', '')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSavingFillDescription(false)
    }
  }

  if (!isLoaded) {
    return null
  }

  return (
    <div className={styles.root}>
      <SectionTitle level={2} className={styles.title}>
        1С Синхронизация
      </SectionTitle>
      <StockConfirmationStatus sellerId={sellerId} />
      <div className={styles.tokenRow}>
        <Input
          readOnly
          value={oneCSettings?.token ?? ''}
          placeholder={TOKEN_PLACEHOLDER}
          className={styles.tokenInput}
        />
        <Button
          type='default'
          onClick={handleCopyToken}
          disabled={!oneCSettings?.token}
        >
          Копировать
        </Button>
      </div>
      <Alert
        type='info'
        showIcon
        icon={<InfoCircleOutlined />}
        message='Скопируйте Ваш API токен и вставьте его в соответствующее поле обработки 1С'
        className={styles.tokenAlert}
      />
      <IntegrationActions
        className={styles.actions}
        actions={[
          {
            label: 'СКАЧАТЬ ОБРАБОТКУ',
            url: ONE_C_PROCESSING_URL,
            icon: <DownloadOutlined />
          },
          {
            label: 'ВИДЕО-ИНСТРУКЦИЯ',
            url: ONE_C_VIDEO_INSTRUCTION_URL,
            icon: <PlayCircleOutlined />
          }
        ]}
      />
      <div className={styles.switchGroup}>
        <div className={styles.switchRow}>
          <Switch
            checked={Boolean(oneCSettings?.create_new_products)}
            loading={isSavingCreateNewProducts}
            onChange={handleCreateNewProductsChange}
          />
          <Typography.Text className={styles.switchLabel}>
            Создавать новые карточки товаров
          </Typography.Text>
        </div>
        <div className={styles.switchRow}>
          <Switch
            checked={oneCSettings?.fill_description_on_create !== false}
            disabled={!oneCSettings?.create_new_products}
            loading={isSavingFillDescription}
            onChange={handleFillDescriptionChange}
          />
          <Typography.Text className={styles.switchLabel}>
            Заполнять описание при создании
          </Typography.Text>
        </div>
      </div>
    </div>
  )
}
